/**
 * Shared anchor navigation: smooth-scrolls on the home page, routes to the
 * home page with a hash from any other page. Respects prefers-reduced-motion
 * and moves keyboard focus to the target section.
 */

import { useCallback } from "react";
import { useLocation, useNavigate } from "react-router-dom";

export const prefersReducedMotion = () =>
  typeof window !== "undefined" &&
  window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;

export const scrollToSection = (hash: string) => {
  const el = document.getElementById(hash);
  if (!el) return false;
  el.scrollIntoView({
    behavior: prefersReducedMotion() ? "auto" : "smooth",
    block: "start",
  });
  window.history.replaceState(null, "", `#${hash}`);
  if (!el.hasAttribute("tabindex")) el.setAttribute("tabindex", "-1");
  el.focus({ preventScroll: true });
  return true;
};

export const useAnchorNav = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const isHome = location.pathname === "/";

  return useCallback(
    (hash: string) => (e: React.MouseEvent) => {
      e.preventDefault();
      if (isHome && scrollToSection(hash)) return;
      navigate(`/#${hash}`);
    },
    [isHome, navigate],
  );
};
